import { Headline } from 'components/Headline';
import { LoadingWrapper } from 'components/LoadingWrapper';

import { useRelatedProducts } from './RelatedProducts.hooks';
import * as S from './RelatedProducts.styles';
import { Product } from './RelatedProducts.types';

function RelatedProductsContent({ products }: { products: Product[] }): React.ReactElement {
  if (!products || products.length === 0) {
    return (
      <S.Container>
        <S.EmptyState>No products associated with this notification.</S.EmptyState>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Note>
        This is a read-only view of the related products. To update the product list, upload a
        new CSV file in the Data tab.
      </S.Note>

      <Headline fontWeight="bold" scale="base" color="black">
        {products.length} product{products.length !== 1 ? 's' : ''}
      </Headline>

      {products.map((product) => (
        <S.ProductCard key={product.id}>
          <S.ProductHeader>
            <S.Field>
              <S.FieldLabel>Item Code</S.FieldLabel>
              <S.FieldValue>{product.itemCode}</S.FieldValue>
            </S.Field>
            <S.Field>
              <S.FieldLabel>Season</S.FieldLabel>
              <S.FieldValue>{product.season}</S.FieldValue>
            </S.Field>
            <S.Field>
              <S.FieldLabel>Season Line</S.FieldLabel>
              <S.FieldValue>{product.seasonLine}</S.FieldValue>
            </S.Field>
            <S.Field>
              <S.FieldLabel>Fabric Code</S.FieldLabel>
              <S.FieldValue>{product.mainFabricCode}</S.FieldValue>
            </S.Field>
            <S.Field>
              <S.FieldLabel>Color Code</S.FieldLabel>
              <S.FieldValue>{product.itemColorCode}</S.FieldValue>
            </S.Field>
          </S.ProductHeader>

          {product.description && (
            <S.DescriptionSection>
              {product.description.it && (
                <S.DescriptionBlock>
                  <S.FieldLabel>Description IT</S.FieldLabel>
                  <S.DescriptionText>{product.description.it}</S.DescriptionText>
                </S.DescriptionBlock>
              )}
              {product.description.en && (
                <S.DescriptionBlock>
                  <S.FieldLabel>Description EN</S.FieldLabel>
                  <S.DescriptionText>{product.description.en}</S.DescriptionText>
                </S.DescriptionBlock>
              )}
            </S.DescriptionSection>
          )}
        </S.ProductCard>
      ))}
    </S.Container>
  );
}

export function RelatedProducts(): React.ReactElement {
  const { products, isLoading } = useRelatedProducts();

  return (
    <LoadingWrapper isLoading={isLoading} hasBackdrop>
      <RelatedProductsContent products={products} />
    </LoadingWrapper>
  );
}
